/** Reglas puras de «varios patrocinadores» y «varios grupos de WhatsApp» del
 * paso Datos (Eduardo 2026-10-01: «así como el botón agregar otra sede…»).
 *
 * El patrocinador 1 y el grupo 1 siguen en sus campos de siempre (banner de
 * patrocinador y «Link Grupo WhatsApp»); estas filas son SOLO los extra, más
 * el nombre/link del patrocinador 1 y el nombre del grupo 1. El servidor
 * repite la validación (imagen subida a este torneo, links http/https). */

import type {
  SponsorBannersInput,
  TournamentSponsorBanner,
  TournamentWhatsappGroup,
  WhatsappGroupsInput,
} from "./api";

export const MAX_SPONSORS = 10;
export const MAX_WHATSAPP_GROUPS = 10;

export interface SponsorRowDraft {
  /** Imagen ya subida (URL del servidor) o null mientras no hay. */
  image_url: string | null;
  /** Alta: archivo elegido que se sube en cuanto existe el torneo. */
  pendingFile: File | null;
  name: string;
  link: string;
}

export interface WhatsappRowDraft {
  label: string;
  url: string;
}

export function emptySponsorRow(): SponsorRowDraft {
  return { image_url: null, pendingFile: null, name: "", link: "" };
}

export function emptyWhatsappRow(): WhatsappRowDraft {
  return { label: "", url: "" };
}

export function isHttpUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/** Link de un patrocinador para usarse en un <a>: solo http/https. */
export function safeSponsorLink(link: string | null | undefined): string | null {
  const trimmed = (link ?? "").trim();
  return isHttpUrl(trimmed) ? trimmed : null;
}

/** Separa la lista completa del servidor: el patrocinador 1 (si el torneo
 * tiene banner en sus campos de siempre) y los extra como filas. */
export function splitSponsorBanners(
  banners: TournamentSponsorBanner[] | null | undefined,
  hasFirstBanner: boolean,
): { firstName: string; firstLink: string; extra: SponsorRowDraft[] } {
  const list = banners ?? [];
  const first = hasFirstBanner ? list[0] : undefined;
  const extras = hasFirstBanner ? list.slice(1) : list;
  return {
    firstName: first?.name ?? "",
    firstLink: first?.link ?? "",
    extra: extras.map((banner) => ({
      image_url: banner.image_url,
      pendingFile: null,
      name: banner.name ?? "",
      link: banner.link ?? "",
    })),
  };
}

/** Igual para los grupos: el grupo 1 es el «Link Grupo WhatsApp» de Datos. */
export function splitWhatsappGroups(
  groups: TournamentWhatsappGroup[] | null | undefined,
  hasFirstGroup: boolean,
): { firstLabel: string; extra: WhatsappRowDraft[] } {
  const list = groups ?? [];
  const first = hasFirstGroup ? list[0] : undefined;
  const extras = hasFirstGroup ? list.slice(1) : list;
  return {
    firstLabel: first?.label ?? "",
    extra: extras.map((group) => ({ label: group.label ?? "", url: group.url })),
  };
}

function sponsorRowIsEmpty(row: SponsorRowDraft): boolean {
  return !row.image_url && !row.pendingFile && !row.name.trim() && !row.link.trim();
}

/** Valida ANTES de llamar al servidor. Filas totalmente vacías se descartan
 * en silencio (se agregó una fila y no se usó); una fila con nombre o link
 * pero sin imagen, o un link que no es http/https, es error. */
export function sponsorRowsError(
  firstLink: string,
  rows: SponsorRowDraft[],
  /** ¿Hay banner principal? Sin él, el primer extra ES el patrocinador 1. */
  hasFirst = true,
): string | null {
  if (firstLink.trim() && !isHttpUrl(firstLink)) {
    return "El link del patrocinador 1 debe iniciar con http:// o https://.";
  }
  let position = hasFirst ? 1 : 0;
  for (const row of rows) {
    if (sponsorRowIsEmpty(row)) continue;
    position += 1;
    if (!row.image_url && !row.pendingFile) {
      return `Al patrocinador ${position} le falta su imagen: súbela o quita ese renglón.`;
    }
    if (row.link.trim() && !isHttpUrl(row.link)) {
      return `El link del patrocinador ${position} debe iniciar con http:// o https://.`;
    }
  }
  if (position > MAX_SPONSORS) return `Máximo ${MAX_SPONSORS} patrocinadores por torneo.`;
  return null;
}

/** Payload del PUT (después de subir los archivos pendientes del alta: aquí
 * solo entran filas con imagen ya subida). */
export function sponsorBannersPayload(
  firstName: string,
  firstLink: string,
  rows: SponsorRowDraft[],
): SponsorBannersInput {
  return {
    first_name: firstName.trim() || null,
    first_link: firstLink.trim() || null,
    extra: rows
      .filter((row) => Boolean(row.image_url))
      .map((row) => ({
        image_url: row.image_url as string,
        name: row.name.trim() || null,
        link: row.link.trim() || null,
      })),
  };
}

export type WhatsappGroupsResult =
  | { ok: true; payload: WhatsappGroupsInput }
  | { ok: false; error: string };

export function prepareWhatsappGroups(
  firstLabel: string,
  rows: WhatsappRowDraft[],
  /** ¿Hay link en «Link Grupo WhatsApp»? Sin él, el primer extra es el 1. */
  hasFirst = true,
): WhatsappGroupsResult {
  const extra: WhatsappGroupsInput["extra"] = [];
  let position = hasFirst ? 1 : 0;
  for (const row of rows) {
    const label = row.label.trim();
    const url = row.url.trim();
    if (!label && !url) continue;
    position += 1;
    if (!url) {
      return { ok: false, error: `Al grupo de WhatsApp ${position} le falta su link: pégalo o quita ese renglón.` };
    }
    if (!isHttpUrl(url)) {
      return { ok: false, error: `El link del grupo de WhatsApp ${position} debe iniciar con http:// o https://.` };
    }
    extra.push({ label: label || null, url: upgradeWhatsappGroupUrl(url) });
  }
  if (position > MAX_WHATSAPP_GROUPS) {
    return { ok: false, error: `Máximo ${MAX_WHATSAPP_GROUPS} grupos de WhatsApp por torneo.` };
  }
  return { ok: true, payload: { first_label: firstLabel.trim() || null, extra } };
}

/** Texto del botón de cada grupo para el jugador. Un solo grupo sin nombre
 * conserva el texto de siempre. */
export function whatsappGroupButtonLabel(
  group: Pick<TournamentWhatsappGroup, "label">,
  index: number,
  total: number,
): string {
  const label = (group.label ?? "").trim();
  if (label) return `Entrar a ${label}`;
  return total > 1 ? `Entrar al grupo ${index + 1} de WhatsApp` : "Entrar al grupo de WhatsApp del torneo";
}

/** ¿Liga de invitación a un grupo de WhatsApp? Solo
 * https://chat.whatsapp.com/<código>, sin usuario en el host. El servidor ya
 * filtra los grupos del jugador; aquí se repite antes de pintar el <a>. */
export function isWhatsappGroupInvite(url: string | null | undefined): boolean {
  const trimmed = (url ?? "").trim();
  if (!trimmed) return false;
  try {
    const parsed = new URL(trimmed);
    return (
      parsed.protocol === "https:" &&
      parsed.host.toLowerCase() === "chat.whatsapp.com" &&
      !parsed.username &&
      !parsed.password &&
      parsed.pathname.replace(/\//g, "") !== ""
    );
  } catch {
    return false;
  }
}

/** Un link de grupo pegado sin la «s» (http://chat.whatsapp.com/…) se guarda
 * con https para que el jugador sí vea su botón. Los demás quedan igual
 * (solo sin espacios). */
export function upgradeWhatsappGroupUrl(url: string | null | undefined): string {
  const trimmed = (url ?? "").trim();
  return /^http:\/\/chat\.whatsapp\.com\//i.test(trimmed)
    ? `https://chat.whatsapp.com/${trimmed.slice("http://chat.whatsapp.com/".length)}`
    : trimmed;
}

/** Aviso en Datos (revisión 2026-10-01): el organizador puede guardar
 * cualquier link, pero el botón «Unirme al grupo de WhatsApp» del jugador
 * solo sale con invitaciones de grupo (https://chat.whatsapp.com/…). */
export const WHATSAPP_GROUP_LINK_WARNING =
  "Este link no es de un grupo de WhatsApp (https://chat.whatsapp.com/…): tus jugadores no verán el botón para unirse.";

export function whatsappGroupLinkWarning(url: string | null | undefined): string | null {
  const trimmed = (url ?? "").trim();
  if (!trimmed) return null;
  return isWhatsappGroupInvite(upgradeWhatsappGroupUrl(trimmed)) ? null : WHATSAPP_GROUP_LINK_WARNING;
}

/** Grupos del botón «Unirme al grupo de WhatsApp» (tarjeta del torneo e
 * inicio del torneo del jugador): solo ligas de WhatsApp; las demás se
 * ignoran. */
export function playerWhatsappGroups(
  groups: TournamentWhatsappGroup[] | null | undefined,
): TournamentWhatsappGroup[] {
  return (groups ?? [])
    .filter((group) => isWhatsappGroupInvite(group?.url))
    .map((group) => ({ label: (group.label ?? "").trim() || null, url: group.url.trim() }));
}

/** Nombre de cada grupo en la lista para elegir («Grupo Roja», «Papás
 * Verde»…); sin nombre, «Grupo 2». */
export function whatsappGroupChoiceLabel(group: Pick<TournamentWhatsappGroup, "label">, index: number): string {
  return (group.label ?? "").trim() || `Grupo ${index + 1}`;
}

/** Grupos que se le muestran al jugador tras inscribirse: la lista nueva si
 * llegó; si no (servidor viejo), el link único de siempre. */
export function submittedWhatsappGroups(
  submitted: { whatsapp_group_link?: string | null; whatsapp_groups?: TournamentWhatsappGroup[] } | null | undefined,
): TournamentWhatsappGroup[] {
  const groups = (submitted?.whatsapp_groups ?? []).filter((group) => (group.url ?? "").trim());
  if (groups.length > 0) return groups;
  const single = submitted?.whatsapp_group_link?.trim();
  return single ? [{ label: null, url: single }] : [];
}

/** Banners de las vistas del jugador: la lista nueva si llegó; si no, el
 * banner único de siempre. */
export function playerBannerList(
  branding: {
    player_banner_url?: string | null;
    player_banners?: Array<{ image_url: string; name: string | null; link: string | null }>;
  } | null | undefined,
): Array<{ image_url: string; name: string | null; link: string | null }> {
  const list = (branding?.player_banners ?? []).filter((banner) => (banner.image_url ?? "").trim());
  if (list.length > 0) return list;
  const single = branding?.player_banner_url?.trim();
  return single ? [{ image_url: single, name: null, link: null }] : [];
}
